/* HighPowered Judge.me fixes — lives in the THEME (not Judge.me's app files) so it
 * survives theme duplication. Three scoped fixes, all via MutationObserver because
 * Judge.me renders its widgets asynchronously:
 *
 *  1. Date format (Swiss DD.MM.YYYY): Judge.me prints DD/MM/YYYY; replace "/" -> ".".
 *     (Selectors per Judge.me support: .jdgm-rev__timestamp, .jdgm-carousel-item__timestamp.)
 *  2. Review-link localization (HIG-74): Judge.me builds product links pointing at the
 *     German apex URL (https://highpowered.ch/products/...). On /en//fr//it/ that drops the
 *     customer back to German. Judge.me can't localize these app-side, so we prefix them with
 *     the active locale root here. Shopify then 301s the German handle to the translated one.
 *  3. All-reviews count separator (HIG-90): the announcement-bar count is formatted by
 *     Judge.me's JS per storefront locale (Italian -> dot "1.716"); force the Swiss
 *     apostrophe everywhere. See fixReviewCount() for details.
 *  4. Announcement-bar badge structure (HIG-112): for EN/FR Judge.me renders the
 *     all-reviews text as <a href="/pages/bewertungen">{rating} <span>({count} word)</span></a>,
 *     which the merchant's custom CSS turns into the short, clickable mobile badge
 *     (it hides the count <span> on mobile and makes the inner <a> clickable). For DE/IT
 *     Judge.me instead emits FLAT text with no inner link/span, so on mobile DE/IT showed
 *     the long desktop string with no clickable link. normalizeAllReviewsBadge() rebuilds
 *     that flat text into the same <a>+<span> structure so the existing CSS produces the
 *     short, clickable badge in all four languages. See normalizeAllReviewsBadge().
 *
 * Scoped to Judge.me containers ([class*="jdgm"]) only — never touches other links.
 */
(function () {
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ROOT_NO_SLASH = ROOT.replace(/\/$/, '');
  // Internal links Judge.me may emit, un-prefixed: /products/, /collections/, /pages/, /blogs/
  var INTERNAL = /^(?:https:\/\/highpowered\.ch)?(\/(?:products|collections|pages|blogs)\/[^\s"]*)$/;

  function fixDates(scope) {
    var els = (scope || document).querySelectorAll('.jdgm-rev__timestamp, .jdgm-carousel-item__timestamp');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      // No persistent flag: Judge.me's CAROUSEL (homepage widget) re-renders the timestamp
      // text AFTER our fix, reverting "." back to "/". A one-time dataset guard would then
      // skip the re-fix and leave the carousel in DD/MM/YYYY (PDP / reviews-page widgets
      // don't re-render, so they were fine). Re-apply whenever a "/" is present; the
      // indexOf check keeps it idempotent (no write once already dotted), so our own
      // textContent change doesn't drive the MutationObserver into a loop.
      if (el.textContent.indexOf('/') !== -1) {
        el.textContent = el.textContent.replace(/\//g, '.');
      }
    }
  }

  function localizeLinks(scope) {
    if (ROOT === '/') return; // German default storefront — no prefix needed
    var links = (scope || document).querySelectorAll('[class*="jdgm"] a[href]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.dataset.hpLocFixed) continue;
      var href = a.getAttribute('href') || '';
      if (href.indexOf(ROOT) === 0) { a.dataset.hpLocFixed = '1'; continue; } // already localized
      var m = href.match(INTERNAL);
      if (m) {
        a.setAttribute('href', ROOT_NO_SLASH + m[1]);
        a.dataset.hpLocFixed = '1';
      }
    }
  }

  // 3. All-reviews count separator (HIG-90): the announcement-bar widget
  //    (.jdgm-all-reviews-text__text) prints "4.8/5 (1'716 Bewertungen)". Judge.me's
  //    JS formats that count by the STOREFRONT locale (it ignores the data-locale='de'
  //    we set), so Italian renders a DOT thousands separator ("1.716") while de/en/fr
  //    use the apostrophe. Normalise the count to the Swiss apostrophe everywhere,
  //    rebuilt from the authoritative integer data-number-of-reviews so the displayed
  //    separator doesn't matter. Only the number inside the parentheses is touched —
  //    the rating ("4.8") is left alone.
  function fixReviewCount(scope) {
    var els = (scope || document).querySelectorAll('.jdgm-all-reviews-text__text');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.hpCountFixed === el.textContent) continue; // already in desired form
      var n = parseInt(el.getAttribute('data-number-of-reviews'), 10);
      if (isNaN(n)) continue;
      var swiss = n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'"); // 1716 -> 1'716
      // Replace the first number token after the opening "(" with the Swiss-formatted
      // count. Separator chars only (dot/comma/apostrophe/nbsp) — NOT a normal space,
      // so the space before the review word is preserved.
      var next = el.textContent.replace(/\((\d[\d.,'\u00a0\u202f\u2009]*)/, '(' + swiss);
      if (next !== el.textContent) el.textContent = next;
      el.dataset.hpCountFixed = el.textContent;
    }
  }

  // 4. Announcement-bar badge structure (HIG-112). Only the header-group widget is
  //    scoped here so the footer all-reviews widget (different responsive rules) is
  //    untouched. Skips elements Judge.me already rendered with an inner <a> (EN/FR,
  //    or our own prior rebuild) so it is idempotent under the MutationObserver.
  function normalizeAllReviewsBadge(scope) {
    var els = (scope || document).querySelectorAll(
      '.shopify-section-group-header-group .jdgm-all-reviews-text__text'
    );
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.querySelector('a')) continue; // already structured (EN/FR or already rebuilt)
      var raw = el.textContent.trim(); // e.g. "4.8/5 (1'718 Bewertungen)"
      if (!raw) continue;
      var m = raw.match(/^(.*?)\s*(\([^)]*\))\s*$/); // ratingPart + "(count word)"
      if (!m) continue; // unexpected format -> leave Judge.me's text as-is (graceful)
      var ratingPart = m[1];
      var countPart = m[2];
      // Normalise the thousands separator inside the parentheses from the authoritative
      // integer (same rule as fixReviewCount) so the rebuilt span keeps the Swiss apostrophe.
      var n = parseInt(el.getAttribute('data-number-of-reviews'), 10);
      if (!isNaN(n)) {
        var swiss = n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        countPart = countPart.replace(/\((\d[\d.,'   ]*)/, '(' + swiss);
      }
      // Reviews page uses the German base handle; Shopify 301s it to the translated
      // handle. Prefix with the active locale root (data-hp-loc-fixed so localizeLinks
      // leaves it alone). No-op prefix on German (ROOT_NO_SLASH === '').
      var a = document.createElement('a');
      a.setAttribute('href', ROOT_NO_SLASH + '/pages/bewertungen');
      a.setAttribute('data-hp-loc-fixed', '1');
      a.appendChild(document.createTextNode(ratingPart + ' '));
      var span = document.createElement('span');
      span.textContent = countPart;
      a.appendChild(span);
      el.textContent = '';
      el.appendChild(a);
    }
  }

  function run() { fixDates(); localizeLinks(); fixReviewCount(); normalizeAllReviewsBadge(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  var obs = new MutationObserver(function () { run(); });
  function start() { obs.observe(document.body, { childList: true, subtree: true }); }
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
